import * as S from './AudioPlayer.styles';
import ReactPlayer from 'react-player';
import { PauseIcon } from '@app/components/common/icons/PauseIcon';
import { VolumeIcon } from '@app/components/common/icons/VolumeIcon';
import { PlayingIcon } from '@app/components/common/icons/PlayingIcon';
import { useEffect, useRef, useState } from 'react';
import { MutedIcon } from '@app/components/common/icons/MutedIcon';

interface PlayerProps {
  audioQuestion: string;
  startPlaying: boolean;
  waitingTime: boolean;
  style?: React.CSSProperties;
  allowDrag: boolean;
  canStart: boolean;
  onEnded: () => void;
  onDurationChange?: (duration: number) => void;
  onStop?: () => void;
}

export const AudioPlayer: React.FC<PlayerProps> = ({
  audioQuestion,
  startPlaying,
  waitingTime,
  style,
  allowDrag,
  canStart,
  onEnded,
  onDurationChange,
  onStop,
}) => {
  const playerRef = useRef<ReactPlayer>(null);
  const [speed, setSpeed] = useState<number>(1);
  const [volume, setVolume] = useState<number>(0.5);
  const [progress, setProgress] = useState<number>(0);
  const [isPlaying, setIsPlaying] = useState<boolean>(false);
  const [currentTime, setCurrentTime] = useState<number>(0);
  const [duration, setDuration] = useState<number>(0);
  const [isDragging, setIsDragging] = useState<boolean>(false);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    if (!isPlaying && onStop) {
      onStop();
    }
    if (isPlaying && !isDragging) {
      intervalRef.current = setInterval(() => {
        if (playerRef.current) {
          const time = playerRef.current.getCurrentTime();
          setCurrentTime(time);
          setProgress((time / duration) * 100);
        }
      }, 500);
    } else {
      if (intervalRef.current) clearInterval(intervalRef.current);
    }

    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [isPlaying, duration, isDragging]);

  useEffect(() => {
    setIsPlaying(startPlaying);
    if (startPlaying) {
      setProgress(0);
      playerRef.current?.seekTo(0);

      // const playButton = document.getElementById('PlayButton');
      // if (playButton) {
      //   playButton.click();
      //   setIsPlaying(startPlaying);
      //   console.log('hello');
      // }
    }
  }, [startPlaying]);

  const togglePlay = () => {
    if (!canStart) return;
    setIsPlaying((prev) => !prev);
  };

  const formatTime = (seconds: number) => {
    const min = Math.floor(seconds / 60);
    const sec = Math.floor(seconds % 60);
    return `${min}:${sec < 10 ? '0' : ''}${sec}`;
  };

  const handleProgress = ({ played, playedSeconds }: { played: number; playedSeconds: number }) => {
    if (!isDragging) {
      setCurrentTime(playedSeconds);
      setProgress(played * 100);
    }
  };

  const handleEnd = () => {
    setIsPlaying(false);
    onEnded();
  };

  const handleDuration = (d: number) => {
    setDuration(d);
    if (onDurationChange) onDurationChange(d);
  };

  // Xử lý kéo thanh progress
  const handleMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
    if (waitingTime || !allowDrag) return;
    setIsDragging(true);
    updateProgress(e);
  };

  const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!isDragging || waitingTime || !allowDrag) return;
    updateProgress(e);
  };

  const handleMouseUp = () => {
    if (waitingTime || !allowDrag) return;
    setIsDragging(false);
    playerRef.current?.seekTo(currentTime, 'seconds');
  };

  const updateProgress = (e: React.MouseEvent<HTMLDivElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const newProgress = Math.min(Math.max(0, (e.clientX - rect.left) / rect.width), 1);
    setProgress(newProgress * 100);
    setCurrentTime(newProgress * duration);
  };

  return (
    <>
      <S.Loaded style={style}>
        <button
          id="PlayButton"
          disabled={waitingTime}
          onClick={togglePlay}
          style={{ cursor: canStart ? 'pointer' : 'not-allowed' }}
        >
          {isPlaying ? <PauseIcon /> : <PlayingIcon style={{ opacity: waitingTime ? 0.5 : 1 }} />}
        </button>
        <span>
          {formatTime(currentTime)} / {formatTime(duration)}
        </span>
        <S.ProgressWrapper onMouseDown={handleMouseDown} onMouseMove={handleMouseMove} onMouseUp={handleMouseUp}>
          <S.ProgressBar style={{ width: `${progress}%` }} />
          <S.Thumb style={{ left: `${progress}%` }} />
        </S.ProgressWrapper>

        <S.VolumeContainer>
          <S.VolumeButton>{volume > 0 ? <VolumeIcon /> : <MutedIcon />}</S.VolumeButton>
          <S.VolumeControl>
            <S.VolumeSlider
              type="range"
              min="0"
              max="1"
              step={0.01}
              value={volume}
              onChange={(e) => setVolume(parseFloat(e.target.value))}
            />
          </S.VolumeControl>
        </S.VolumeContainer>

        {canStart && (
          <S.SpeedContainer>
            <select id="speed" value={speed} onChange={(e) => setSpeed(parseFloat(e.target.value))}>
              <option value={0.5}>0.5x</option>
              <option value={0.8}>0.8x</option>
              <option value={1.0}>1.0x</option>
              <option value={1.2}>1.2x</option>
              <option value={1.5}>1.5x</option>
            </select>
          </S.SpeedContainer>
        )}
      </S.Loaded>

      <ReactPlayer
        style={{ display: 'none' }}
        ref={playerRef}
        url={audioQuestion}
        playing={isPlaying}
        controls={false}
        playsinline={true}
        config={{
          file: {
            attributes: { preload: 'auto' },
            forceAudio: true,
          },
        }}
        playbackRate={speed}
        volume={volume}
        onProgress={handleProgress}
        onDuration={handleDuration}
        onEnded={handleEnd}
        stopOnUnmount={true}
        // onReady={() => {
        //   document.querySelector('iframe')?.setAttribute('allow', 'autoplay');
        // }}
      />
    </>
  );
};
